import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { Toast, type ToastTone } from './Toast';

export type ToastInput = {
  title: string;
  description?: string;
  tone?: ToastTone;
  /**
   * Auto-dismiss ms.
   * Default: 10000 for danger, 5000 otherwise. Pass 0 to stick until dismiss.
   */
  duration?: number;
};

type ToastRecord = ToastInput & { id: string };

type ToastContextValue = {
  toast: (input: ToastInput | string) => string;
  dismiss: (id: string) => void;
};

const ToastContext = createContext<ToastContextValue | null>(null);

let externalToast: ToastContextValue['toast'] | null = null;

/**
 * Imperative helper — works after ToastProvider mounts (product UiContext shape).
 * Prefer `useToast().toast` inside React trees.
 */
export function toast(input: ToastInput | string): string {
  if (!externalToast) {
    console.warn('toast() called outside ToastProvider');
    return '';
  }
  return externalToast(input);
}

function defaultDuration(tone: ToastTone | undefined): number {
  // Errors linger longer so users can read what went wrong.
  return tone === 'danger' ? 10000 : 5000;
}

export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<ToastRecord[]>([]);
  const timers = useRef<Map<string, number>>(new Map());

  const dismiss = useCallback((id: string) => {
    setItems((prev) => prev.filter((t) => t.id !== id));
    const t = timers.current.get(id);
    if (t != null) {
      window.clearTimeout(t);
      timers.current.delete(id);
    }
  }, []);

  const toastFn = useCallback(
    (input: ToastInput | string) => {
      const payload: ToastInput =
        typeof input === 'string' ? { title: input, tone: 'default' } : input;
      const id = `toast-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
      const duration = payload.duration ?? defaultDuration(payload.tone);
      setItems((prev) => {
        const combined = [...prev, { ...payload, id }];
        const dropped = combined.slice(0, Math.max(0, combined.length - 5));
        for (const item of dropped) {
          const handle = timers.current.get(item.id);
          if (handle == null) continue;
          window.clearTimeout(handle);
          timers.current.delete(item.id);
        }
        return combined.slice(-5);
      });
      if (duration > 0) {
        const handle = window.setTimeout(() => dismiss(id), duration);
        timers.current.set(id, handle);
      }
      return id;
    },
    [dismiss],
  );

  externalToast = toastFn;

  const value = useMemo(
    () => ({ toast: toastFn, dismiss }),
    [toastFn, dismiss],
  );

  return (
    <ToastContext.Provider value={value}>
      {children}
      <div
        className="pointer-events-none fixed top-5 right-4 z-[60] flex w-full max-w-md flex-col gap-2"
        aria-live="polite"
      >
        {items.map((item) => (
          <div key={item.id} className="pointer-events-auto">
            <Toast
              title={item.title}
              {...(item.description != null
                ? { description: item.description }
                : {})}
              {...(item.tone != null ? { tone: item.tone } : {})}
              onDismiss={() => dismiss(item.id)}
            />
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    throw new Error('useToast must be used within ToastProvider');
  }
  return ctx;
}
